// THE WALLET SHEET IS WHERE THE CAR IS (18 Oct 2026, docs/DECISIONS.md).
//
// Robin: "yea I think we need to be able to get the details from apple or
// google pay" (two payers had bounced at the typed fields), then "fewest
// boxes and fields is what Im thinking..". So a wallet payer types nothing on
// Book & pay: the Apple Pay / Google Pay / Link sheet asks for the address,
// the phone and the email, and its address is the car's location. The card
// path keeps its typed postcode, house number and mobile.
//
// While the sheet is open it hands over a REDACTED address (country, city,
// part of the postcode); the server checks it (POST
// /api/v2/orders/wallet_address, Order::WalletAddressCheck, which also
// writes the refusal's words) and the sheet's total is brought up to date
// before Face ID. At `confirm` the full address and the phone arrive and go
// through the ordinary embedded checkout.
//
// Pure, so it runs under node --test (wallet-address.test.mjs). No em dashes:
// the labels are shown in the wallet sheet. Kept small: the booking bundle
// has a 250 KB budget (bin/check-bundle-size).
import { isFullPostcode, normalisePostcode } from './postcode.js'
import { isValidUkMobile, normaliseUkMobile } from './mobile.js'

// The one "shipping" rate: where the wash happens, free. A district that can
// be in the Congestion Charge zone gets a second rate the payer picks
// (Order::WalletAddressCheck: the district says "maybe", never "is").
export const WASH_RATE = 'wash_here'
export const WASH_RATE_ZONE = 'wash_here_zone'
export const WASH_RATE_LABEL = 'Wash at this address'
export const WASH_RATE_ZONE_LABEL = 'Wash at this address, in the Congestion Charge Zone'
export const washRate = () => ({ id: WASH_RATE, displayName: WASH_RATE_LABEL, amount: 0 })

// The sheet's line for the booking itself (lineItems), with the rate beside it.
export const WASH_LINE_LABEL = 'GoWashMyCar wash'

// Said on the page for a sheet address with no whole UK postcode (the
// server's own refusals carry their words).
export const NO_POSTCODE = 'Choose an address with a full UK postcode.'

// The outward code of a whole postcode ("SW6 1RE" -> "SW6"), the only part
// of an address an event may carry; the server reads it from the redacted
// one while the sheet is open.
export const outwardOf = postcode => (isFullPostcode(postcode) ? normalisePostcode(postcode).split(' ')[0] : null)

// The answer to the server's check, as the sheet needs it.
//   check:          the wallet_address answer's data, or null when it could
//                   not be asked (network): the booking then goes on at the
//                   page's price, never blocked by our own outage.
//   pageAmountPence the total on the page (its congestion tick included)
//   pageZone        whether the page's congestion tick is on (a typed or
//                   remembered postcode); it picks which rate comes first.
// Returns { ok: false, reason, message } to reject, or { ok: true, outward,
// basePence, shippingRates, lineItems, amountPence, priceChanged, ... }.
export const walletAddressOutcome = ({ check, pageAmountPence = 0, pageZone = false } = {}) => {
  const page = Number(pageAmountPence) || 0
  if (check && check.ok === false) {
    return { ok: false, reason: check.reason || 'no_postcode', message: check.message || NO_POSTCODE, outward: check.outward || null }
  }
  const zonePence = Math.max(0, Number(check?.congestion_pence) || 0)
  const inZone = !!check?.may_be_in_congestion_zone && zonePence > 0
  // The page's total without its own zone tick: the sheet's address decides.
  const base = Math.max(0, page - (pageZone ? zonePence : 0))
  const rates = [washRate()]
  if (inZone) rates[pageZone ? 'unshift' : 'push']({ id: WASH_RATE_ZONE, displayName: WASH_RATE_ZONE_LABEL, amount: zonePence })
  const amountPence = base + rates[0].amount
  return {
    ok: true,
    outward: check?.outward || null,
    mayBeInZone: inZone,
    basePence: base,
    shippingRates: rates,
    lineItems: [{ name: WASH_LINE_LABEL, amount: base }],
    amountPence,
    priceChanged: amountPence !== page
  }
}

// The total once the payer picks a rate in the sheet.
export const amountForRate = (basePence, rate) => (Number(basePence) || 0) + Math.max(0, Number(rate?.amount) || 0)

// Whether the chosen rate is the zone one: true / false, or null when no rate
// came back (the page's own tick then stands).
export const zoneFromRate = rate => (rate?.id === WASH_RATE_ZONE ? true : rate?.id === WASH_RATE ? false : null)

// The car's location from the confirm event's shippingAddress: line1 (house
// number and street, as the wallet holds it), line2, the town; the postcode
// on its own (the order puts it on the end, once). A line1 with no house
// number is used as it is; nothing blocks. Only a whole postcode is taken,
// never the redacted half.
export const walletAddressFromShipping = shipping => {
  const a = shipping?.address || {}
  const postcode = isFullPostcode(a.postal_code) ? normalisePostcode(a.postal_code) : ''
  const lines = []
  for (const raw of [a.line1, a.line2, a.city]) {
    const line = String(raw || '').trim()
    if (line && !lines.some(l => l.toLowerCase() === line.toLowerCase())) lines.push(line)
  }
  const address = lines.join(', ')
  return { address, postcode, outward: outwardOf(postcode), complete: !!(address && postcode) }
}

// The wallet's phone as the mobile, by the booking form's own rules
// (lib/mobile.js, which the server mirrors in UkMobile): "+44 7766 554433"
// -> "07766 554433". null when it is not a UK mobile.
export const walletMobile = phone => (isValidUkMobile(phone) ? normaliseUkMobile(phone) : null)
